import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { PublisherModel } from '@Entities/work/publisher-model'
import { RightsholderContacts } from '@Entities/rightsholder/rightsholder-contact'
import { TableRowData } from '@Components/ViewWork/Table'
import React, { ForwardedRef, useContext, useEffect, useMemo } from 'react'
import { StoresContext } from '@App/contexts'
import GroupedTable, {
    GroupedTableInfo,
} from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/GroupedPublishersTable/GroupedTable'
import { ICONS } from '@Const/common'
import { generatePublishersTableSchema } from '@Entities/schemas/wizardFlow/publishers-table-schema'
import { Maybe } from '@Types'
import { GroupedPublishers } from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/GroupedPublishersTable'
import { getPublicContacts } from '@App/api/routes/rightsholder/contact'
import { cloneDeep } from 'lodash'
import { observer } from 'mobx-react'
import { getPublisherTableRows } from '@App/helpers/tables/publisher-tables'

interface PublisherTableRowsProps {
    groupedPublisherInfo: GroupedTableInfo[][] | undefined
    setGroupedPublisherInfo: (info: GroupedTableInfo[][] | undefined) => void
    allowShareEditing: boolean
    updateShare: (model: OriginalPublisherModel, newValue: number, adminModel?: PublisherModel) => void
    setShareCount: (count: number) => void
    onValidation: (isValid: boolean) => void
    ref: ForwardedRef<HTMLDivElement>
}

const PublishersTableRows = ({
    groupedPublisherInfo,
    setGroupedPublisherInfo,
    onValidation,
    allowShareEditing,
    updateShare,
    setShareCount,
    ref,
}: PublisherTableRowsProps) => {
    const { rightsholderStore, overclaimViewStore } = useContext(StoresContext)
    const initialWork = useMemo(
        () => cloneDeep(overclaimViewStore.overclaimViewItem),
        [overclaimViewStore.overclaimViewItem]
    )

    useEffect(() => {
        let isSubscribed = true

        if (!allowShareEditing) {
            overclaimViewStore.setOverclaimViewItem(initialWork)
        }

        publisherTableInfo().then(({ groupedTableInfo, totalShares }) => {
            if (isSubscribed) {
                setGroupedPublisherInfo(groupedTableInfo)
                setShareCount(totalShares)
            }
        })

        return () => {
            isSubscribed = false
        }
    }, [allowShareEditing])

    const setupPublicContacts = async (ipids: number[]): Promise<RightsholderContacts | undefined> => {
        if (ipids?.length) {
            const response = await getPublicContacts(ipids)
            return response.data ?? undefined
        }
        return undefined
    }

    const publisherTableInfo = async (): Promise<{ groupedTableInfo: GroupedTableInfo[][]; totalShares: number }> => {
        const groupedPublishers: GroupedPublishers = overclaimViewStore.overclaimViewItem?.originalPublishers.reduce(
            (prev: any, curr) => {
                ;(prev[String(curr.topMemberName)] = prev[String(curr.topMemberName)] || []).push(curr)
                return prev
            },
            {}
        )

        const result: GroupedTableInfo[][] = []
        let totalShares = 0
        for (const topMember of Object.keys(groupedPublishers)) {
            const admins: Array<{ publisherModel: OriginalPublisherModel; adminModel: PublisherModel }> = []
            const adminIpids: Array<Maybe<number>> = []
            const originalIpids: Array<Maybe<number>> = []
            const nonAdminsPubs: Array<{
                publisherModel: OriginalPublisherModel
                adminModel: PublisherModel | undefined
            }> = []
            let groupedShare = 0
            groupedPublishers[topMember].forEach((model: OriginalPublisherModel) => {
                const hasAdmin: boolean = model.administratorPublishers.length > 0

                if (hasAdmin) {
                    model.administratorPublishers.forEach((admin) => {
                        admins.push({ publisherModel: model, adminModel: admin })
                        adminIpids.push(admin.topMemberIpId)
                        groupedShare += Number(admin.publisherShare)
                    })
                } else {
                    nonAdminsPubs.push({ publisherModel: model, adminModel: undefined })
                    originalIpids.push(model.topMemberIpId)
                    groupedShare += Number(model.publisherShare)
                }
            })

            const ipids = [...adminIpids, ...originalIpids].filter((ipid): ipid is number => !!ipid)
            const contacts = await setupPublicContacts(ipids)
            const adminRows = getPublisherTableRows(
                admins,
                contacts,
                updateShare,
                rightsholderStore.currentRightsholderId,
                true,
                allowShareEditing
            )
            const nonAdminRows = getPublisherTableRows(
                nonAdminsPubs,
                contacts,
                updateShare,
                rightsholderStore.currentRightsholderId,
                false,
                allowShareEditing
            )

            const memberStatus =
                groupedPublishers[topMember].find((publisher) => publisher.memberStatus)?.memberStatus ?? 'PENDING'
            const statusReason = groupedPublishers[topMember].find((publisher) => publisher.statusReason)?.statusReason
            const documentationDueDate = groupedPublishers[topMember].find(
                (publisher) => publisher.documentationDueDate
            )?.documentationDueDate

            const tableRowToGroupedTableInfo = (row: TableRowData): GroupedTableInfo => ({
                groupedBy: groupedPublishers[topMember][0].topMemberName,
                groupedValue: groupedShare,
                memberStatus,
                statusReason,
                documentationDueDate,
                headerRow: {
                    row: [
                        { text: 'Name' },
                        { text: 'P Number' },
                        { text: 'IPI' },
                        { text: 'Represented Writers' },
                        { text: 'Collection Share' },
                        { text: 'Contact Details' },
                    ],
                },
                rows: [row],
            })

            const adminRowsMapped = adminRows.map(tableRowToGroupedTableInfo)
            const nonAdminRowsMapped = nonAdminRows.map(tableRowToGroupedTableInfo)
            totalShares += groupedShare
            result.push([...adminRowsMapped, ...nonAdminRowsMapped])
        }
        return { groupedTableInfo: result, totalShares }
    }

    return (
        <>
            {groupedPublisherInfo?.map((publisher: GroupedTableInfo[], index: number) => (
                <GroupedTable
                    key={index}
                    tableInfo={publisher}
                    icon={ICONS.account}
                    validationSchema={
                        overclaimViewStore.overclaimViewItem &&
                        generatePublishersTableSchema(
                            overclaimViewStore.overclaimViewItem,
                            rightsholderStore.currentRightsholderId
                        )
                    }
                    rowsAreValid={(isValid) => onValidation(isValid)}
                    ref={ref}
                />
            ))}
        </>
    )
}

export default observer(PublishersTableRows)
